import { useMemo, useState, type ReactNode } from 'react'
import { Typography } from 'antd'
import {
  AimOutlined,
  BookOutlined,
  CopyOutlined,
  DiffOutlined,
  MessageOutlined,
  NodeIndexOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { PageHeader } from '../components/ui'
import './Home.css'

const { Text } = Typography

type TabKey = 'all' | 'chat' | 'search' | 'graph' | 'dedup' | 'compare' | 'locate' | 'desensitize' | 'knowledge' | 'enterprise'

/** 仅包含前台应用入口 */
type AppItem = {
  id: string
  title: string
  desc: string
  to: string
  tab: TabKey[]
  icon: ReactNode
  iconBg: string
}

const APPS: AppItem[] = [
  {
    id: 'chat',
    title: 'AI 对话',
    desc: '多轮对话，基于企业知识库检索增强生成，支持上下文连续追问。',
    to: '/chat',
    tab: ['all', 'chat'],
    icon: <MessageOutlined />,
    iconBg: '#2563eb',
  },
  {
    id: 'search',
    title: '智能搜索',
    desc: '向量 + 关键词混合检索相关片段，快速定位制度、流程与文档要点。',
    to: '/search',
    tab: ['all', 'search'],
    icon: <SearchOutlined />,
    iconBg: '#fa8c16',
  },
  {
    id: 'graph',
    title: '知识图谱',
    desc: '可视化实体与关系网络，发现知识条目之间的关联与引用路径。',
    to: '/graph',
    tab: ['all', 'graph'],
    icon: <NodeIndexOutlined />,
    iconBg: '#eb2f96',
  },
  {
    id: 'dedup',
    title: '文件查重',
    desc: '相似度查重统计重复率并标红片段；精确查重识别完全相同的附件与正文。',
    to: '/dedup',
    tab: ['all', 'dedup'],
    icon: <CopyOutlined />,
    iconBg: '#13c2c2',
  },
  {
    id: 'locate',
    title: '文档定位',
    desc: '按模板解析文档字段，点击结果在 PDF / Word 原文中标红定位，支持证据追溯。',
    to: '/locate',
    tab: ['all', 'locate'],
    icon: <AimOutlined />,
    iconBg: '#fa8c16',
  },
  {
    id: 'compare',
    title: '文档比对',
    desc: '上传或从企业库选两篇文档，文本细粒度 diff（含标点、空行）或图片像素级热力图比对，可导出报告。',
    to: '/compare',
    tab: ['all', 'compare'],
    icon: <DiffOutlined />,
    iconBg: '#2f54eb',
  },
  {
    id: 'desensitize',
    title: '文档脱敏',
    desc: '上传 PDF，识别姓名、住址、手机号、身份证、银行卡等敏感信息并脱敏，支持脱敏前后全文对照。',
    to: '/desensitize',
    tab: ['all', 'desensitize'],
    icon: <SafetyCertificateOutlined />,
    iconBg: '#389e0d',
  },
  {
    id: 'enterprise',
    title: '企业知识库',
    desc: '企业文档库：文件夹管理、多策略切片、ES8 全文检索与 RAG 问答。',
    to: '/enterprise',
    tab: ['all', 'enterprise', 'knowledge'],
    icon: <TeamOutlined />,
    iconBg: '#722ed1',
  },
  {
    id: 'knowledge',
    title: '个人知识库',
    desc: '上传文档，自动切片向量化，供对话与检索使用。',
    to: '/knowledge',
    tab: ['all', 'knowledge'],
    icon: <BookOutlined />,
    iconBg: '#52c41a',
  },
]

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'chat', label: '对话' },
  { key: 'search', label: '检索' },
  { key: 'graph', label: '图谱' },
  { key: 'dedup', label: '查重' },
  { key: 'compare', label: '比对' },
  { key: 'desensitize', label: '脱敏' },
  { key: 'locate', label: '定位' },
  { key: 'enterprise', label: '企业库' },
  { key: 'knowledge', label: '个人库' },
]

function AppCard({ item }: { item: AppItem }) {
  return (
    <Link to={item.to} className="home-agent-card">
      <div className="home-agent-icon" style={{ background: item.iconBg }}>
        {item.icon}
      </div>
      <div className="home-agent-body">
        <Text strong className="home-agent-title">
          {item.title}
        </Text>
        <Text type="secondary" className="home-agent-desc">
          {item.desc}
        </Text>
      </div>
    </Link>
  )
}

export default function Home() {
  const [activeTab, setActiveTab] = useState<TabKey>('all')

  const tabCounts = useMemo(() => {
    const counts = {} as Record<TabKey, number>
    for (const t of TABS) {
      counts[t.key] = APPS.filter((a) => a.tab.includes(t.key)).length
    }
    return counts
  }, [])

  const filtered = useMemo(() => APPS.filter((a) => a.tab.includes(activeTab)), [activeTab])

  return (
    <div className="home-page public-page-scroll">
      <PageHeader
        level={2}
        className="home-hero-header"
        title="AI 应用中心"
        description="基于企业私有知识库，提供对话、检索与知识管理能力。"
      />

      <div className="home-banner">
        <div className="home-banner-text">
          <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 8 }}>
            企业知识库 · 智能应用
          </Text>
          <Text type="secondary">
            支持文档上传、OCR、切片向量化与混合检索；前台面向业务用户，后台完成系统配置与权限管理。
          </Text>
        </div>
        <div className="home-banner-art" aria-hidden>
          AI
        </div>
      </div>

      <div className="home-tabs-bar">
        <div className="home-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={`home-tab${activeTab === t.key ? ' home-tab-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}({tabCounts[t.key]})
            </button>
          ))}
        </div>
      </div>

      <div className="home-grid">
        {filtered.map((item) => (
          <AppCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  )
}
